import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Descriptions,
  Form,
  Input,
  Spin,
  Modal,
  Table,
  Divider,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import {
  useStageInspection,
  useStageInspectionMeasurements,
  useStartInspection,
  useCompleteStageInspection,
  useReleaseInspectionHoldPoint,
  useCompleteInspectionWitness,
  useInspectionCheckpoints,
  useInspectionAttachments,
} from '../../hooks/useQCWorkflow';
import { useSystemSettings } from '../../hooks/useSettings';
import { useWorkOrder } from '../../hooks/useWorkOrder';
import { StageInspectionMeasurement } from '../../services/qc-workflow.service';
import workOrderService from '../../services/workOrder.service';
import InspectionCheckpoints from '../../components/quality/InspectionCheckpoints';
import InspectionAttachments from '../../components/quality/InspectionAttachments';
import { PrintableStageInspection } from '../../components/quality/PrintableStageInspection';
import InspectionWorkLineDetailsCard from '../../components/quality/InspectionWorkLineDetailsCard';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_STAGE_INSPECTION } from '@/constants/approval-entity-types';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    Scheduled: 'blue',
    Pending: 'default',
    'In Progress': 'processing',
    'Pending Approval': 'gold',
    Completed: 'success',
    Failed: 'error',
    'On Hold': 'warning',
    Cancelled: 'error',
  };
  return colors[status] || 'default';
};

const getResultColor = (result: string) => {
  const colors: Record<string, string> = {
    Pass: 'success',
    Fail: 'error',
    Conditional: 'warning',
    Pending: 'default',
  };
  return colors[result] || 'default';
};

const InspectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [completeForm] = Form.useForm();
  const [witnessForm] = Form.useForm();
  const [isCompleteModalVisible, setIsCompleteModalVisible] = useState(false);

  const { data: sci, isLoading, refetch } = useStageInspection(id || '');
  const { data: workOrder } = useWorkOrder(sci?.work_order_id || '');
  const { data: measurements = [] } = useStageInspectionMeasurements(sci?.id || '');
  const { data: settings } = useSystemSettings();
  const { data: printCheckpoints = [] } = useInspectionCheckpoints(id || '');
  const { data: printAttachments = [] } = useInspectionAttachments(id || '');

  const woId = sci?.work_order_id || '';
  const lineId = sci?.work_order_line_item_id || sci?.workOrderLineItem?.id || '';
  const { data: boqOps, isLoading: boqOpsLoading } = useQuery({
    queryKey: ['work-orders', woId, 'boq-operations'],
    queryFn: () => workOrderService.getBoqOperations(woId),
    enabled: Boolean(woId),
  });

  const workLineFromWo = workOrder?.lineItems?.find((li) => li.id === lineId);
  const boqLine = boqOps?.lines?.find((l) => l.work_order_line_item_id === lineId);
  const workLine = {
    ...(workLineFromWo || {}),
    ...(sci?.workOrderLineItem || {}),
    revision:
      sci?.workOrderLineItem?.revision ??
      workLineFromWo?.revision ??
      boqLine?.revision ??
      null,
    item_code:
      sci?.workOrderLineItem?.item_code ?? workLineFromWo?.item_code ?? boqLine?.item_code ?? null,
    item_description:
      sci?.workOrderLineItem?.item_description ??
      workLineFromWo?.item_description ??
      boqLine?.item_description ??
      null,
    line_number:
      sci?.workOrderLineItem?.line_number ??
      workLineFromWo?.line_number ??
      boqLine?.line_number ??
      null,
  };
  const lineOperations = boqLine?.operations || [];

  const startMutation = useStartInspection();
  const completeMutation = useCompleteStageInspection();
  const releaseHoldMutation = useReleaseInspectionHoldPoint();
  const completeWitnessMutation = useCompleteInspectionWitness();

  const isCompleted = sci?.status === 'Completed' || sci?.status === 'Failed';
  const isInProgress = sci?.status === 'In Progress';
  const isPendingApproval = sci?.status === 'Pending Approval';
  const isLocked = isCompleted || isPendingApproval;
  const hasDimensions = sci?.has_dimensions !== false;

  useEffect(() => {
    if (sci && sci.status === 'Pending') {
      startMutation.mutateAsync(sci.id).then(() => refetch()).catch(() => {});
    }
  }, [sci?.id, sci?.status]);

  const handleComplete = async (values: any) => {
    if (!sci) return;
    try {
      await completeMutation.mutateAsync({
        id: sci.id,
        data: {
          findings: values.findings,
          observations: values.observations,
        },
      });
      setIsCompleteModalVisible(false);
      completeForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error completing inspection:', error);
    }
  };

  const handleReleaseHoldPoint = async () => {
    if (!sci) return;
    await releaseHoldMutation.mutateAsync({
      inspectionId: sci.id,
      notes: 'Hold point released from inspection detail page',
    });
    refetch();
  };

  const handleWitnessSignOff = async (values: any) => {
    if (!sci) return;
    await completeWitnessMutation.mutateAsync({
      inspectionId: sci.id,
      data: {
        witness_name: values.witness_name,
        witness_party: values.witness_party,
        witness_signature: { timestamp: new Date().toISOString(), signature_data: values.signature },
      },
    });
    witnessForm.resetFields();
    refetch();
  };

  const handlePrint = () => {
    if (!sci) return;
    const companySettings = settings?.filter((s: any) => s.category === 'company') || [];
    const getSetting = (key: string) => companySettings.find((s: any) => s.setting_key === key)?.setting_value ?? '';
    const companyInfo = {
      name: getSetting('company_name') || 'Company Name',
      address: getSetting('company_address') || '',
      phone: getSetting('company_phone') || '',
      email: getSetting('company_email') || '',
      taxNumber: getSetting('tax_registration_number') ? `TRN: ${getSetting('tax_registration_number')}` : '',
    };
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    const woNum = workOrder?.work_order_number || sci.workOrder?.work_order_number || sci.work_order_id;
    root.render(
      <PrintableStageInspection
        sci={sci}
        workOrderNumber={woNum}
        checkpoints={printCheckpoints}
        measurements={measurements}
        attachments={printAttachments}
        companyInfo={companyInfo}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  const measurementColumns: ColumnsType<StageInspectionMeasurement> = [
    { title: 'Unit', dataIndex: 'unit_sequence', key: 'unit_sequence', width: 80 },
    { title: 'Dimension', dataIndex: ['stageDimension', 'dimension_name'], key: 'dimension_name' },
    { title: 'Nominal', key: 'nominal', render: (_, record) => record.stageDimension?.nominal_value || '-' },
    { title: 'Measured', dataIndex: 'measured_value', key: 'measured_value' },
    { title: 'Deviation', dataIndex: 'deviation', key: 'deviation', render: (v: number) => v?.toFixed(3) || '-' },
    { title: 'Result', dataIndex: 'result', key: 'result', render: (r: string) => <Tag color={getResultColor(r)}>{r}</Tag> },
  ];

  if (!id) return <div style={{ padding: 24 }}>No inspection ID provided</div>;
  if (isLoading) return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  if (!sci) return <div style={{ padding: 24 }}>Inspection not found</div>;

  return (
    <div style={{ padding: '24px' }}>
      {/* ─── Header ─── */}
      <Space style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/inspections')}>
          Back
        </Button>
        <Button icon={<PrinterOutlined />} onClick={handlePrint}>
          Print
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          Inspection: {sci.inspection_number}
        </Title>
        {sci.inspection_source === 'quality_plan' && (
          <Tag color="purple">Quality Plan</Tag>
        )}
      </Space>

      {/* ─── 1. Inspection Summary ─── */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Status</Text>
                <br />
                <Tag color={getStatusColor(sci.status)} style={{ marginTop: 4 }}>{sci.status}</Tag>
              </div>
              <div>
                <Text type="secondary">Result</Text>
                <br />
                <Tag color={getResultColor(sci.result)} style={{ marginTop: 4 }}>{sci.result}</Tag>
              </div>
              {sci.is_hold_point && (
                <div>
                  <Tag color="red">Hold Point</Tag>
                  {sci.hold_point_released ? <Tag color="green">Released</Tag> : <Tag color="orange">Unreleased</Tag>}
                </div>
              )}
              {sci.witness_required && (
                <div>
                  <Tag color="blue">Witness Required</Tag>
                  {sci.witness_signature ? <Tag color="green">Signed</Tag> : <Tag color="orange">Awaiting</Tag>}
                </div>
              )}
            </Space>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Inspection Cycle</Text>
                <br />
                <Text strong>{sci.inspection_cycle ?? 1}</Text>
              </div>
              <div>
                <Text type="secondary">Category</Text>
                <br />
                <Text strong>{sci.inspection_category || 'General'}</Text>
              </div>
              <div>
                <Text type="secondary">Inspection Date</Text>
                <br />
                <Text>{sci.inspection_date ? dayjs(sci.inspection_date).format('DD/MM/YYYY HH:mm') : 'Not started'}</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Created</Text>
                <br />
                <Text>{dayjs(sci.created_at).format('DD/MM/YYYY HH:mm')}</Text>
              </div>
              <div>
                <Text type="secondary">Last Updated</Text>
                <br />
                <Text>{dayjs(sci.updated_at).format('DD/MM/YYYY HH:mm')}</Text>
              </div>
              <div>
                <Text type="secondary">Inspector</Text>
                <br />
                <Text strong>{sci.inspector?.name || sci.inspector?.email || 'N/A'}</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Work Order & line / operations */}
      <InspectionWorkLineDetailsCard
        title="Work Order & Operation"
        workOrderNumber={
          workOrder?.work_order_number || sci.workOrder?.work_order_number || sci.work_order_id
        }
        currentOperationLabel={
          sci.workOrderStage
            ? `${sci.workOrderStage.stage_code} — ${sci.workOrderStage.stage_name}`
            : sci.work_order_stage_id || null
        }
        line={workLine}
        showOperations
        operations={lineOperations}
        operationsLoading={boqOpsLoading}
      />

      {/* ─── 2. Inspection Checkpoints ─── */}
      <Card title="Inspection Checkpoints" style={{ marginBottom: 16 }}>
        <InspectionCheckpoints inspectionId={sci.id} readOnly={isLocked} />
      </Card>

      {/* ─── 3. Dimension Measurements (Optional) ─── */}
      {hasDimensions && (
        <Card
          title="Dimension Measurements"
          style={{ marginBottom: 16 }}
          extra={
            isInProgress && (
              <Button
                type="primary"
                icon={<ExperimentOutlined />}
                onClick={() =>
                  navigate(
                    `/qms/dimensional-inspection?workOrderId=${sci.work_order_id}&stageId=${sci.work_order_stage_id}`
                  )
                }
              >
                Open Dimensional Inspection
              </Button>
            )
          }
        >
          {measurements.length > 0 ? (
            <Table
              columns={measurementColumns}
              dataSource={measurements}
              rowKey={(record) => `${record.unit_sequence}-${record.stage_dimension_id}`}
              pagination={false}
              size="small"
            />
          ) : (
            <Text type="secondary">
              No dimension measurements recorded yet.
              {isInProgress && ' Use "Open Dimensional Inspection" to record measurements.'}
            </Text>
          )}
        </Card>
      )}

      {/* ─── 4. Attachments / Photos ─── */}
      <Card title="Attachments & Photos" style={{ marginBottom: 16 }}>
        <InspectionAttachments inspectionId={sci.id} readOnly={isLocked} />
      </Card>

      {/* ─── 5. Approval / Remarks ─── */}
      {!isLocked && isInProgress && (
        <Card title="Inspection Actions" style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setIsCompleteModalVisible(true)}
            >
              Submit for Approval
            </Button>
          </Space>
        </Card>
      )}

      {isPendingApproval && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Pending approval"
          description={`Awaiting approval of result ${sci.pending_result || ''}. The stage is released or rejected once the final approver signs off. Use the Approval panel or Approval Inbox.`}
        />
      )}

      {sci.approval_instance_id && (
        <Card title="Approval" size="small" style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_STAGE_INSPECTION}
            entityId={sci.id}
            extraInvalidateQueryKeys={[
              ['stage-inspection', sci.id],
              ['stage-inspections'],
              ['work-orders'],
            ]}
          />
        </Card>
      )}

      {/* Hold-Point Release */}
      {sci.is_hold_point && !sci.hold_point_released && isCompleted && sci.result === 'Pass' && (
        <Card title="Hold-Point Release" style={{ marginBottom: 16, borderColor: '#faad14' }}>
          <Text>This inspection is a hold point and requires explicit release before the operation can proceed.</Text>
          <br /><br />
          <Button
            type="primary"
            danger
            onClick={handleReleaseHoldPoint}
            loading={releaseHoldMutation.isPending}
          >
            Release Hold Point
          </Button>
        </Card>
      )}

      {/* Witness Sign-Off */}
      {sci.witness_required && !sci.witness_signature && sci.result !== 'Pending' && (
        <Card title="Witness Sign-Off" style={{ marginBottom: 16 }}>
          <Text type="secondary">Witness Party: {sci.witness_party || 'TBD'}</Text>
          <Form form={witnessForm} layout="vertical" onFinish={handleWitnessSignOff} style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="witness_name"
                  label="Witness Name"
                  rules={[{ required: true, message: 'Enter witness name' }]}
                >
                  <Input placeholder="Enter witness name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="witness_party"
                  label="Witness Party"
                  rules={[{ required: true, message: 'Enter witness party' }]}
                >
                  <Input placeholder="e.g., Customer, Third Party Inspector" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="signature" label="Digital Signature">
                  <TextArea rows={2} placeholder="Signature acknowledgment or verification code" />
                </Form.Item>
              </Col>
            </Row>
            <Button type="primary" htmlType="submit" loading={completeWitnessMutation.isPending}>
              Submit Witness Sign-Off
            </Button>
          </Form>
        </Card>
      )}

      {/* Witness info (already signed) */}
      {sci.witness_signature && (
        <Card title="Witness Information" style={{ marginBottom: 16 }}>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Witness Name">{sci.witness_name || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Witness Party">{sci.witness_party || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Witness Date">
              {sci.witness_date ? dayjs(sci.witness_date).format('DD MMM YYYY') : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Status"><Tag color="success">Signed</Tag></Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Findings & Observations (completed) */}
      {(sci.findings || sci.observations) && (
        <Card title="Findings & Observations" style={{ marginBottom: 16 }}>
          {sci.findings && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Findings:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <Text>{sci.findings}</Text>
              </div>
            </div>
          )}
          {sci.observations && (
            <div>
              <Text strong>Observations:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <Text>{sci.observations}</Text>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Rejection / Rework */}
      {sci.rejection_reason && (
        <Card title="Rejection Details" style={{ marginBottom: 16, borderColor: '#ffa39e' }}>
          <div style={{ marginBottom: 12 }}>
            <Text strong>Rejection Reason:</Text>
            <div style={{ marginTop: 8, padding: 12, background: '#fff1f0', borderRadius: 4 }}>
              <Text>{sci.rejection_reason}</Text>
            </div>
          </div>
          {sci.rework_notes && (
            <div>
              <Text strong>Rework Notes:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#fffbe6', borderRadius: 4 }}>
                <Text>{sci.rework_notes}</Text>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* NCR Information */}
      {sci.ncr_generated && (
        <Card style={{ marginBottom: 16, backgroundColor: '#fff1f0', borderColor: '#ffa39e' }}>
          <Space>
            <Tag color="error">NCR GENERATED</Tag>
            <Text>
              An NCR has been automatically generated due to inspection failure.
              {sci.ncr?.ncr_number && ` NCR: ${sci.ncr.ncr_number}`}
            </Text>
          </Space>
        </Card>
      )}

      {/* Complete Inspection Modal */}
      <Modal
        title="Submit Inspection for Approval"
        open={isCompleteModalVisible}
        okText="Submit"
        onOk={() => completeForm.submit()}
        onCancel={() => setIsCompleteModalVisible(false)}
        width={600}
        confirmLoading={completeMutation.isPending}
      >
        <Form form={completeForm} layout="vertical" onFinish={handleComplete}>
          <Form.Item name="findings" label="Findings">
            <TextArea rows={4} placeholder="Enter inspection findings" />
          </Form.Item>
          <Form.Item name="observations" label="Observations">
            <TextArea rows={4} placeholder="Enter observations" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InspectionDetailPage;
